// SCR-STAFF-503 (US-CLN-050، 051، 054): طرح درمان — ایجاد از قالب پکیج (جستجوی سروری) یا مراحل دستی با خدمات (جستجوی سروری کاتالوگ)، نسخه‌بندی هر
// تغییر، «ایجاد برآورد» (BIL)، پذیرش، توقف، ادامه و قطع با دلیل، و پیشرفت (جلسات انجام‌شده، نوبت‌های آتی، عقب‌ماندگی از برنامه).
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  unwrap,
  type ClnPlanStageRequest,
  type ClnPlanTemplateView,
  type ClnTreatmentPlanListItem,
  type ClnTreatmentPlanRequest,
  type ClnTreatmentPlanView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, faDigits, FormDialog, LoadingState, PageHeader, ReasonDialog, toJalali, toJalaliDateTime, useDisplaySettings, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { PatientHeader } from '../patients/PatientHeader';
import { beautyKeys, parameterSummary, PlanStatusChip, planPath, SCR_STAFF_503, useCatalogServiceSearch, type ServiceOption } from './beautyCommon';
import { clnKeys, num } from './common';

interface StageDraft {
  stageId: string | null;
  title: string;
  services: ServiceOption[];
  sessions: string;
  minIntervalDays: string;
  maxIntervalDays: string;
}

const EMPTY_STAGE: StageDraft = { stageId: null, title: '', services: [], sessions: '1', minIntervalDays: '', maxIntervalDays: '' };

function intOrNull(value: string): number | null {
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

/** درخواست طرح از فرم (مراحل خالی در ایجاد از قالب = مراحل پکیج در سرور). */
export function planRequest(title: string, goals: string, recommendations: string, stages: readonly StageDraft[], sourcePackageId: string | null): ClnTreatmentPlanRequest {
  return {
    title: title.trim(),
    goals: goals
      .split('\n')
      .map((goal) => goal.trim())
      .filter(Boolean),
    diagnosisIds: [],
    recommendations: recommendations.trim() || null,
    stages: stages.map(
      (stage): ClnPlanStageRequest => ({
        stageId: stage.stageId,
        title: stage.title.trim() || null,
        serviceIds: stage.services.map((service) => service.id),
        sessions: intOrNull(stage.sessions) ?? 1,
        minIntervalDays: intOrNull(stage.minIntervalDays),
        maxIntervalDays: intOrNull(stage.maxIntervalDays),
        providerId: null,
      }),
    ),
    sourcePackageId,
    providerId: null,
  };
}

function ServicePicker({ value, onChange, testId }: { value: ServiceOption[]; onChange: (next: ServiceOption[]) => void; testId: string }) {
  const { t } = useTranslation('cln');
  const search = useCatalogServiceSearch();
  return (
    <Autocomplete
      multiple
      size="small"
      options={search.services}
      value={value}
      filterOptions={(options) => options}
      loading={search.isFetching}
      isOptionEqualToValue={(option, item) => option.id === item.id}
      getOptionLabel={(option) => option.name}
      onInputChange={(_event, text, reason) => reason === 'input' && search.search(text)}
      onChange={(_event, next) => onChange(next)}
      renderInput={(params) => <TextField {...params} label={t('cln:plan.services')} slotProps={{ htmlInput: { ...params.inputProps, 'data-testid': testId } }} />}
    />
  );
}

export interface PlanEditorDialogProps {
  patientId: string;
  plan?: ClnTreatmentPlanView;
  onClose: () => void;
  onSaved?: (plan: ClnTreatmentPlanView) => void;
}

/** ایجاد یا ویرایش طرح (AC-CLN-050.1، 050.2). */
export function PlanEditorDialog({ patientId, plan, onClose, onSaved }: PlanEditorDialogProps) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [title, setTitle] = useState(plan?.title ?? '');
  const [goals, setGoals] = useState((plan?.goals ?? []).join('\n'));
  const [recommendations, setRecommendations] = useState(plan?.recommendations ?? '');
  const [template, setTemplate] = useState<ClnPlanTemplateView | null>(null);
  const [templateQuery, setTemplateQuery] = useState('');
  const [stages, setStages] = useState<StageDraft[]>(
    plan
      ? plan.stages.map((stage) => ({
          stageId: stage.id,
          title: stage.title ?? '',
          services: stage.serviceIds.map((id, index) => ({ id, name: stage.serviceNames[index] ?? id, code: '' })),
          sessions: String(num(stage.sessions) ?? 1),
          minIntervalDays: stage.minIntervalDays === null || stage.minIntervalDays === undefined ? '' : String(stage.minIntervalDays),
          maxIntervalDays: stage.maxIntervalDays === null || stage.maxIntervalDays === undefined ? '' : String(stage.maxIntervalDays),
        }))
      : [{ ...EMPTY_STAGE }],
  );
  const [touched, setTouched] = useState(false);
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(templateQuery.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [templateQuery]);
  const templates = useQuery({
    queryKey: beautyKeys.templates(session?.tenantId, debounced),
    enabled: !plan,
    staleTime: 60_000,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/plan-templates', { params: { query: { q: debounced || undefined, limit: 20 } } })),
  });
  const save = useMutation({
    mutationFn: () => {
      const body = planRequest(title, goals, recommendations, template && !plan ? [] : stages, plan ? plan.sourcePackageId : (template?.packageId ?? null));
      return plan
        ? unwrap(tenantApi.PUT('/api/v1/clinical/treatment-plans/{id}', { params: { path: { id: plan.id } }, body }))
        : unwrap(tenantApi.POST('/api/v1/clinical/patients/{patientId}/treatment-plans', { params: { path: { patientId } }, headers: { 'Idempotency-Key': key.current() }, body }));
    },
    onSettled: (_data, error) => key.settle(error),
    onSuccess: (view) => {
      void queryClient.invalidateQueries({ queryKey: beautyKeys.plans(session?.tenantId, patientId) });
      void queryClient.invalidateQueries({ queryKey: beautyKeys.plan(session?.tenantId, view.id) });
      onSaved?.(view);
      onClose();
    },
  });
  // قالب پکیج: مراحل در سرور از پکیج ساخته می‌شوند (C-AGENT-883)؛ ویرایش مراحل پس از ایجاد با نسخه تازه.
  const applyTemplate = (value: ClnPlanTemplateView | null) => {
    setTemplate(value);
    if (value && !title.trim()) {
      setTitle(value.name);
    }
  };
  const update = (index: number, patch: Partial<StageDraft>) => setStages((current) => current.map((stage, position) => (position === index ? { ...stage, ...patch } : stage)));
  const invalid = !title.trim() || (!template && stages.some((stage) => stage.services.length === 0 || (intOrNull(stage.sessions) ?? 0) < 1));
  const submit = () => {
    setTouched(true);
    if (!invalid) {
      save.mutate();
    }
  };
  return (
    <FormDialog open maxWidth="md" screenId={SCR_STAFF_503} titleKey={plan ? 'cln:plan.edit' : 'cln:plan.new'} onSubmit={submit} onClose={onClose} isDirty={Boolean(title || goals)} isSubmitting={save.isPending} submitError={save.error}>
      {!plan && (
        <Autocomplete
          size="small"
          options={templates.data?.items ?? []}
          value={template}
          filterOptions={(options) => options}
          loading={templates.isFetching}
          isOptionEqualToValue={(option, value) => option.packageId === value.packageId}
          getOptionLabel={(option) => `${option.name} (${option.code})`}
          onInputChange={(_event, value, reason) => reason === 'input' && setTemplateQuery(value)}
          onChange={(_event, value) => applyTemplate(value)}
          renderInput={(params) => <TextField {...params} label={t('cln:plan.template')} helperText={t('cln:plan.template_hint')} slotProps={{ htmlInput: { ...params.inputProps, 'data-testid': 'cln-plan-template' } }} />}
        />
      )}
      {plan && (plan.status === 'Accepted' || plan.status === 'InProgress') && <Alert severity="info">{t('cln:plan.financial_hint')}</Alert>}
      <TextField fullWidth required label={t('cln:plan.title_field')} value={title} error={touched && !title.trim()} onChange={(event) => setTitle(event.target.value)} slotProps={{ htmlInput: { maxLength: 200, 'data-testid': 'cln-plan-title' } }} />
      <TextField fullWidth multiline minRows={2} label={t('cln:plan.goals')} helperText={t('cln:plan.goals_hint')} value={goals} onChange={(event) => setGoals(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'cln-plan-goals' } }} />
      <TextField fullWidth multiline minRows={2} label={t('cln:plan.recommendations')} value={recommendations} onChange={(event) => setRecommendations(event.target.value)} slotProps={{ htmlInput: { maxLength: 2000 } }} />
      <Typography variant="subtitle2">{t('cln:plan.stages')}</Typography>
      {template && !plan && (
        <Stack spacing={0.5} data-testid="cln-plan-template-stages">
          {template.stages.map((stage, index) => (
            <Typography key={`${template.packageId}-${index}`} variant="body2">
              {stage.title ?? t('cln:plan.stage_n', { n: faDigits(index + 1) })} · {t('cln:plan.sessions_value', { n: faDigits(num(stage.sessions) ?? 1) })}
            </Typography>
          ))}
        </Stack>
      )}
      {!template && stages.map((stage, index) => (
        <Paper key={stage.stageId ?? `new-${index}`} variant="outlined" sx={{ p: 1.5 }} data-testid="cln-plan-stage">
          <Grid container spacing={1}>
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField fullWidth size="small" label={t('cln:plan.stage_title')} value={stage.title} onChange={(event) => update(index, { title: event.target.value })} />
            </Grid>
            <Grid size={{ xs: 4, sm: 2 }}>
              <TextField
                fullWidth
                size="small"
                required
                label={t('cln:plan.sessions')}
                value={stage.sessions}
                error={touched && (intOrNull(stage.sessions) ?? 0) < 1}
                onChange={(event) => update(index, { sessions: event.target.value.replace(/[^\d]/g, '') })}
                slotProps={{ htmlInput: { inputMode: 'numeric', 'data-testid': `cln-plan-sessions-${index}` } }}
              />
            </Grid>
            <Grid size={{ xs: 4, sm: 2 }}>
              <TextField fullWidth size="small" label={t('cln:plan.min_interval')} value={stage.minIntervalDays} onChange={(event) => update(index, { minIntervalDays: event.target.value.replace(/[^\d]/g, '') })} slotProps={{ htmlInput: { inputMode: 'numeric' } }} />
            </Grid>
            <Grid size={{ xs: 4, sm: 2 }}>
              <TextField fullWidth size="small" label={t('cln:plan.max_interval')} value={stage.maxIntervalDays} onChange={(event) => update(index, { maxIntervalDays: event.target.value.replace(/[^\d]/g, '') })} slotProps={{ htmlInput: { inputMode: 'numeric' } }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 1 }}>
              {stages.length > 1 && (
                <IconButton aria-label={t('cln:plan.remove_stage')} onClick={() => setStages((current) => current.filter((_item, position) => position !== index))}>
                  <DeleteOutlineIcon />
                </IconButton>
              )}
            </Grid>
            <Grid size={12}>
              <ServicePicker value={stage.services} onChange={(services) => update(index, { services })} testId={`cln-plan-services-${index}`} />
            </Grid>
          </Grid>
        </Paper>
      ))}
      {!template && (
        <Button onClick={() => setStages((current) => [...current, { ...EMPTY_STAGE }])} data-testid="cln-plan-add-stage">
          {t('cln:plan.add_stage')}
        </Button>
      )}
    </FormDialog>
  );
}

/** طرح‌های یک مراجعه‌کننده (برگه پرونده و مواجهه). */
export function TreatmentPlansPanel({ patientId, compact = false }: { patientId: string; compact?: boolean }) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const plans = useQuery({
    queryKey: beautyKeys.plans(session?.tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/patients/{patientId}/treatment-plans', { params: { path: { patientId } } })),
  });
  const canManage = access.accessMode === 'Full' && access.can('cln.plan.manage');
  if (compact) {
    const active = (plans.data?.items ?? []).filter((plan) => plan.status !== 'Completed' && plan.status !== 'Discontinued' && plan.status !== 'Declined');
    return (
      <Stack spacing={0.5} data-testid="cln-plans-compact">
        {active.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        )}
        {active.map((plan) => (
          <Link key={plan.id} component={RouterLink} to={planPath(plan.id)} variant="body2">
            {plan.title} · {faDigits(num(plan.completedSessions) ?? 0)}/{faDigits(num(plan.totalSessions) ?? 0)}
            {plan.behindSchedule ? ` · ${t('cln:plan.behind')}` : ''}
          </Link>
        ))}
        {canManage && (
          <Button size="small" sx={{ alignSelf: 'flex-start' }} onClick={() => setCreating(true)} data-testid="cln-plan-new">
            {t('cln:plan.new')}
          </Button>
        )}
        {creating && <PlanEditorDialog patientId={patientId} onClose={() => setCreating(false)} onSaved={(plan) => void navigate(planPath(plan.id))} />}
      </Stack>
    );
  }
  const columns: DataTableColumn<ClnTreatmentPlanListItem>[] = [
    { id: 'title', headerKey: 'cln:plan.title_field', cell: (row) => row.title },
    { id: 'status', headerKey: 'cln:plan.status', cell: (row) => <PlanStatusChip status={row.status} /> },
    {
      id: 'progress',
      headerKey: 'cln:plan.progress',
      cell: (row) => (
        <>
          {faDigits(num(row.completedSessions) ?? 0)}/{faDigits(num(row.totalSessions) ?? 0)}
          {row.behindSchedule ? <Chip size="small" color="warning" label={t('cln:plan.behind')} sx={{ marginInlineStart: 1 }} /> : null}
        </>
      ),
    },
    { id: 'created', headerKey: 'cln:plan.created', cell: (row) => toJalali(row.createdAt) },
  ];
  return (
    <Stack spacing={1} data-testid="cln-plans-panel">
      {canManage && (
        <Stack direction="row">
          <Button variant="outlined" onClick={() => setCreating(true)} data-testid="cln-plan-new">
            {t('cln:plan.new')}
          </Button>
        </Stack>
      )}
      <DataTable
        columns={columns}
        rows={plans.data?.items}
        getRowId={(row) => row.id}
        isLoading={plans.isPending}
        error={plans.error}
        onRetry={() => void plans.refetch()}
        onRowClick={(row) => void navigate(planPath(row.id))}
        captionKey="cln:plan.list"
        emptyTitleKey="cln:plan.empty"
      />
      {creating && <PlanEditorDialog patientId={patientId} onClose={() => setCreating(false)} onSaved={(plan) => void navigate(planPath(plan.id))} />}
    </Stack>
  );
}

/** صفحه طرح و پیشرفت (/desk/plans/:id). */
export function TreatmentPlanPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const { timeZone } = useDisplaySettings();
  const queryClient = useQueryClient();
  const quoteKey = useIdempotencyKey();
  const tenantId = session?.tenantId;
  const [dialog, setDialog] = useState<'edit' | 'hold' | 'discontinue' | null>(null);
  const progress = useQuery({
    queryKey: [...beautyKeys.plan(tenantId, id), 'progress'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/treatment-plans/{id}/progress', { params: { path: { id } } })),
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: beautyKeys.plan(tenantId, id) });
    if (progress.data) {
      void queryClient.invalidateQueries({ queryKey: beautyKeys.plans(tenantId, progress.data.plan.patientId) });
    }
  };
  const action = useMutation({
    mutationFn: async ({ kind, reason }: { kind: 'quote' | 'accept' | 'hold' | 'resume' | 'discontinue'; reason?: string }) => {
      const path = { params: { path: { id } } };
      switch (kind) {
        case 'quote':
          return unwrap(tenantApi.POST('/api/v1/clinical/treatment-plans/{id}:request-quote', { ...path, headers: { 'Idempotency-Key': quoteKey.current() } }));
        case 'accept':
          return unwrap(tenantApi.POST('/api/v1/clinical/treatment-plans/{id}:accept', path));
        case 'hold':
          return unwrap(tenantApi.POST('/api/v1/clinical/treatment-plans/{id}:hold', { ...path, body: { reason: reason ?? null } }));
        case 'resume':
          return unwrap(tenantApi.POST('/api/v1/clinical/treatment-plans/{id}:resume', path));
        default:
          return unwrap(tenantApi.POST('/api/v1/clinical/treatment-plans/{id}:discontinue', { ...path, body: { reason: reason ?? null } }));
      }
    },
    onSettled: (_data, error, variables) => (variables.kind === 'quote' ? quoteKey.settle(error) : undefined),
    onSuccess: () => {
      setDialog(null);
      refresh();
      void queryClient.invalidateQueries({ queryKey: clnKeys.pending(tenantId) });
    },
  });

  if (progress.isPending) {
    return <LoadingState rows={4} />;
  }
  if (progress.error || !progress.data) {
    return <ErrorState error={progress.error} onRetry={() => void progress.refetch()} />;
  }
  const { plan, sessions, upcoming, versions } = progress.data;
  const canManage = access.accessMode === 'Full' && access.can('cln.plan.manage');
  const total = num(plan.totalSessions) ?? 0;
  const done = num(plan.completedSessions) ?? 0;
  const open = plan.status !== 'Completed' && plan.status !== 'Discontinued' && plan.status !== 'Declined';
  return (
    <Stack spacing={2} data-screen-id={SCR_STAFF_503} data-testid="cln-plan-page" data-status={plan.status}>
      <PatientHeader patientId={plan.patientId} compact />
      <PageHeader
        titleKey="cln:plan.page_title"
        titleValues={{ title: plan.title }}
        badges={<PlanStatusChip status={plan.status} />}
        actions={
          canManage && (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {open && (
                <Button onClick={() => setDialog('edit')} data-testid="cln-plan-edit">
                  {t('cln:plan.edit')}
                </Button>
              )}
              {(plan.status === 'Draft' || plan.status === 'Proposed' || plan.financialChangePending) && (
                <Button variant="outlined" onClick={() => action.mutate({ kind: 'quote' })} disabled={action.isPending} data-testid="cln-plan-quote">
                  {t('cln:plan.request_quote')}
                </Button>
              )}
              {(plan.status === 'Draft' || plan.status === 'Proposed') && (
                <Button variant="outlined" onClick={() => action.mutate({ kind: 'accept' })} disabled={action.isPending} data-testid="cln-plan-accept">
                  {t('cln:plan.accept')}
                </Button>
              )}
              {(plan.status === 'Accepted' || plan.status === 'InProgress') && (
                <Button onClick={() => setDialog('hold')} data-testid="cln-plan-hold">
                  {t('cln:plan.hold')}
                </Button>
              )}
              {plan.status === 'OnHold' && (
                <Button onClick={() => action.mutate({ kind: 'resume' })} disabled={action.isPending} data-testid="cln-plan-resume">
                  {t('cln:plan.resume')}
                </Button>
              )}
              {open && (
                <Button color="error" onClick={() => setDialog('discontinue')} data-testid="cln-plan-discontinue">
                  {t('cln:plan.discontinue')}
                </Button>
              )}
            </Stack>
          )
        }
      />
      {action.error ? <ErrorState error={action.error} compact /> : null}
      {plan.behindScheduleSince && (
        <Alert severity="warning" data-testid="cln-plan-behind">
          {t('cln:plan.behind_since', { date: toJalali(plan.behindScheduleSince) })}
        </Alert>
      )}
      {plan.financialChangePending && <Alert severity="info">{t('cln:plan.financial_pending')}</Alert>}
      {plan.quoteNumber && (
        <Typography variant="body2" data-testid="cln-plan-quote-number">
          {t('cln:plan.quote', { number: plan.quoteNumber, status: plan.quoteStatus ?? '' })}
        </Typography>
      )}
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack spacing={1}>
          <Typography variant="body2" data-testid="cln-plan-progress">
            {t('cln:plan.progress_value', { done: faDigits(done), total: faDigits(total) })}
          </Typography>
          <LinearProgress variant="determinate" value={total ? Math.min(100, (done / total) * 100) : 0} aria-label={t('cln:plan.progress')} />
          {plan.goals.length > 0 && <Typography variant="body2">{t('cln:plan.goals')}: {plan.goals.join(t('cln:common.list_separator'))}</Typography>}
          {plan.recommendations && <Typography variant="body2">{plan.recommendations}</Typography>}
        </Stack>
      </Paper>
      <Grid container spacing={2}>
        {plan.stages.map((stage) => (
          <Grid key={stage.id} size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="cln-plan-stage-view">
              <Typography variant="subtitle2">{stage.title ?? t('cln:plan.stage_n', { n: faDigits(num(stage.ordinal) ?? 1) })}</Typography>
              <Typography variant="body2">{stage.serviceNames.join(t('cln:common.list_separator'))}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('cln:plan.stage_progress', {
                  done: faDigits(num(stage.completedSessions) ?? 0),
                  booked: faDigits(num(stage.bookedSessions) ?? 0),
                  total: faDigits(num(stage.sessions) ?? 0),
                })}
                {stage.minIntervalDays !== null && stage.minIntervalDays !== undefined
                  ? ` · ${t('cln:plan.interval', { min: faDigits(num(stage.minIntervalDays) ?? 0), max: faDigits(num(stage.maxIntervalDays) ?? 0) })}`
                  : ''}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
      <Typography variant="subtitle1" component="h3">
        {t('cln:plan.sessions_done')}
      </Typography>
      <DataTable
        columns={[
          { id: 'at', headerKey: 'cln:plan.session_at', cell: (row) => toJalaliDateTime(row.completedAt, { timeZone }) },
          { id: 'n', headerKey: 'cln:plan.session_number', cell: (row) => (row.numberInStage ? faDigits(num(row.numberInStage) ?? 0) : '—') },
          { id: 'areas', headerKey: 'cln:session.areas', cell: (row) => row.areas.map((area) => t(`cln:body_area.${area}`)).join(t('cln:common.list_separator')) || '—' },
          { id: 'params', headerKey: 'cln:session.parameters', cell: (row) => parameterSummary(row.parameters) || '—' },
          { id: 'outcome', headerKey: 'cln:session.outcome', cell: (row) => row.outcome ?? '—' },
        ]}
        rows={sessions}
        getRowId={(row) => row.sessionId}
        captionKey="cln:plan.sessions_done"
        emptyTitleKey="cln:plan.no_sessions"
      />
      <Typography variant="subtitle1" component="h3">
        {t('cln:plan.upcoming')}
      </Typography>
      {upcoming.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('cln:plan.no_upcoming')}
        </Typography>
      ) : (
        upcoming.map((row) => (
          <Typography key={row.appointmentId} variant="body2" data-testid="cln-plan-upcoming">
            {toJalaliDateTime(row.start, { timeZone })}
          </Typography>
        ))
      )}
      <Typography variant="subtitle1" component="h3">
        {t('cln:plan.versions')}
      </Typography>
      {versions.map((version) => (
        <Typography key={String(version.version)} variant="body2" color="text.secondary" data-testid="cln-plan-version">
          {t('cln:plan.version_row', {
            version: faDigits(num(version.version) ?? 0),
            reason: t(`cln:plan.version_reason.${version.reason}`, { defaultValue: version.reason }),
            at: toJalaliDateTime(version.recordedAt, { timeZone }),
            by: version.recordedByName ?? '',
          })}
        </Typography>
      ))}
      {dialog === 'edit' && <PlanEditorDialog patientId={plan.patientId} plan={plan} onClose={() => setDialog(null)} onSaved={refresh} />}
      <ReasonDialog
        open={dialog === 'hold' || dialog === 'discontinue'}
        titleKey={dialog === 'hold' ? 'cln:plan.hold' : 'cln:plan.discontinue'}
        messageKey="cln:plan.reason_message"
        destructive={dialog === 'discontinue'}
        busy={action.isPending}
        error={action.error}
        onConfirm={(reason) => action.mutate({ kind: dialog === 'hold' ? 'hold' : 'discontinue', reason })}
        onCancel={() => setDialog(null)}
      />
    </Stack>
  );
}
